import { createHttpClient } from './shared/httpClient';

const getGraphqlEndpoint = () =>
  process.env.NODE_ENV === 'development'
    ? '/api/graphql'
    : `${process.env.REACT_APP_WORKPLACE_GRAPHQL_URL}/graphql`;

const workplaceClient = createHttpClient({ withAuth: true });

// Location service for fetching location/place data
export interface HierarchyItem {
  id: string;
  name: string;
  placeTypeId?: string;
  parentPlaceId?: string;
}

export interface Place {
  id: string;
  name: string;
  hierarchy: HierarchyItem[];
}

export interface AllPlacesResponse {
  allPlaces: {
    places: Place[];
  };
}

// GraphQL query for fetching all places/locations for an organization
const ALL_PLACES_QUERY = `
  query AllPlaces($orgId: ID!) {
    allPlaces(orgId: $orgId, limit: 1000) {
      places {
        id
        name
        hierarchy {
          id
          name
          placeTypeId
          parentPlaceId
        }
      }
    }
  }
`;

/**
 * Fetch all locations/places for a specific organization
 * 
 * @param orgId - Organization ID
 * @returns Promise<Place[]> - Array of places with hierarchy
 */
export const fetchLocations = async (orgId: string): Promise<Place[]> => {
  try {
    const data = await workplaceClient.post<{ data: { allPlaces: { places: Place[] } }; errors?: unknown[] }>(
      getGraphqlEndpoint(),
      {
        body: { operationName: 'AllPlaces', variables: { orgId }, query: ALL_PLACES_QUERY },
        mode: 'cors',
        credentials: 'include',
        headers: { Origin: window.location.origin },
      }
    );

    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    const places: Place[] = data.data.allPlaces.places;

    // The API returns only assignable places (desks, rooms, floors). Structural
    // ancestors (regions, buildings, some floors) appear only inside each place's
    // hierarchy array and are never returned as top-level entries, which makes
    // them unreachable by the tree walker. Reconstruct them from the hierarchy
    // data that is already present in the response.
    const placeIds = new Set(places.map(p => p.id));
    const syntheticAncestors = new Map<string, Place>();

    places.forEach(place => {
      place.hierarchy.forEach((node, index) => {
        if (!placeIds.has(node.id) && !syntheticAncestors.has(node.id)) {
          syntheticAncestors.set(node.id, {
            id: node.id,
            name: node.name,
            hierarchy: place.hierarchy.slice(0, index + 1),
          });
        }
      });
    });

    return [...syntheticAncestors.values(), ...places];
  } catch (error) {
    console.error('Error fetching locations:', error);
    // Return empty array as fallback
    return [];
  }
};

/**
 * Get location by ID from a list of places
 * 
 * @param places - Array of places
 * @param locationId - Location ID to find
 * @returns Place | undefined - The found place or undefined
 */
export const getLocationById = (places: Place[], locationId: string): Place | undefined => {
  return places.find(place => place.id === locationId);
};

/**
 * Build a hierarchical path string for a location
 * 
 * @param place - The place to build the path for
 * @returns string - Hierarchical path (e.g., "Building A / Floor 2 / Room 201")
 */
export const getLocationPath = (place: Place): string => {
  const pathParts = place.hierarchy.map(h => h.name);
  
  // Add the place's own name if it's not already in the hierarchy
  if (pathParts.length === 0 || pathParts[pathParts.length - 1] !== place.name) {
    pathParts.push(place.name);
  }
  
  return pathParts.join(' / ');
};

/**
 * Convert Place to the Location format expected by LocationTree
 */
export const placeToLocation = (place: Place) => {
  return {
    id: place.id,
    name: place.name,
    hierarchy: place.hierarchy
  };
};

/**
 * Convert array of Places to Locations for LocationTree
 */
export const placesToLocations = (places: Place[]) => {
  return places.map(placeToLocation);
};

/**
 * Sort places in hierarchical order
 * Sites first, then buildings, floors, and finally rooms/desks
 * Within each level, sort alphabetically by full path
 */
export const sortPlacesHierarchically = (places: Place[]): Place[] => {
  return [...places].sort((a, b) => {
    // First, sort by hierarchy depth (sites first, then buildings, etc.)
    const depthDiff = a.hierarchy.length - b.hierarchy.length;
    if (depthDiff !== 0) {
      return depthDiff;
    }
    
    // Then sort by full path for items at the same depth
    const pathA = getLocationPath(a);
    const pathB = getLocationPath(b);
    return pathA.localeCompare(pathB);
  });
};